import { useEffect, useRef } from "react";

/**
 * Hook to prevent iOS zoom on input focus
 * Temporarily increases font size to 16px to prevent auto-zoom
 */
export function usePreventZoom() {
  const elementRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Only apply on iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    const handleFocus = () => {
      const originalSize = element.style.fontSize;
      element.style.fontSize = '16px';
      
      const handleBlur = () => {
        element.style.fontSize = originalSize;
        element.removeEventListener('blur', handleBlur);
      };
      
      element.addEventListener('blur', handleBlur);
    };

    element.addEventListener('focus', handleFocus);

    return () => {
      element.removeEventListener('focus', handleFocus);
    };
  }, []);

  return elementRef;
}

/**
 * Hook to handle iOS safe area insets
 */
export function useIOSSafeArea() {
  useEffect(() => {
    // Set viewport-fit=cover for iOS
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      const content = viewport.getAttribute('content') || '';
      if (!content.includes('viewport-fit')) {
        viewport.setAttribute('content', `${content}, viewport-fit=cover`);
      }
    }

    // Add iOS-specific class to body
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      document.body.classList.add('ios-device');
    }
  }, []);
}
